import { useEffect, useRef } from 'react';
import { Alert } from '@publicplan/kern-react-kit';

export interface FormError {
  /** ID of the invalid field, so the entry can link to it. */
  fieldId: string;
  message: string;
}

export interface FormErrorSummaryProps {
  errors: FormError[];
  title?: string;
  /**
   * Moves focus to the summary when it appears.
   * Deliberately not called `autoFocus`: this is about focusing the error area after a
   * failed submit, not about an automatically focused input.
   */
  focusOnMount?: boolean;
}

/**
 * Error summary at the top of a form.
 *
 * WCAG 3.3.1 (BITV) requires errors to be identified and reachable: this list collects all
 * errors, links each one to its field and takes focus when it appears — otherwise screen
 * reader users would not notice the failed submit.
 *
 * The default title is German, the portal's UI language.
 */
export const FormErrorSummary = ({
  errors,
  title = 'Ihre Eingaben konnten nicht übernommen werden',
  focusOnMount = true,
}: FormErrorSummaryProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const hasErrors = errors.length > 0;

  useEffect(() => {
    if (hasErrors && focusOnMount) {
      ref.current?.focus();
    }
  }, [hasErrors, focusOnMount, errors]);

  if (!hasErrors) {
    return null;
  }

  return (
    // The KERN alert already carries role="alert" and announces itself. The wrapper is only
    // focusable so that focus can move here after a submit.
    <div ref={ref} tabIndex={-1} className="vf-error-summary">
      <Alert variant="danger">
        <Alert.Header title={title} hasIcon />
        <Alert.Body>
          <ul className="vf-error-summary__list">
            {errors.map(error => (
              <li key={error.fieldId}>
                <a className="kern-link" href={`#${error.fieldId}`}>
                  {error.message}
                </a>
              </li>
            ))}
          </ul>
        </Alert.Body>
      </Alert>
    </div>
  );
};
